/**
 * Browser Disguise + Discreet Filename presets — single source of truth.
 *
 * When the member picks a disguise, we swap the browser tab title AND
 * the favicon. Favicons are tiny inline SVGs to avoid extra network
 * calls (data-light per founder guidance).
 */

// Inline SVG favicons — data-light (~200 bytes each).
const svgFavicon = (paths, colour = '#0f172a') =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='${colour}'>${paths}</svg>`
  )}`;

const NOTEBOOK = svgFavicon(
  "<path d='M4 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4V4z'/><path d='M7 7h9M7 11h9M7 15h6' stroke='white' stroke-width='1.4' stroke-linecap='round'/>"
);
const RECORDS = svgFavicon(
  "<path d='M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z'/><path d='M15 2v5h5' stroke='white' stroke-width='1.4' fill='none'/>"
);
const BOOK = svgFavicon(
  "<path d='M4 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4V3z'/><path d='M4 3v18M8 7h8M8 11h8M8 15h5' stroke='white' stroke-width='1.4' fill='none' stroke-linecap='round'/>"
);
const FOLDER = svgFavicon(
  "<path d='M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z'/>"
);

export const BROWSER_DISGUISES = [
  { key: 'myinvestfolio',   label: 'MyInvestFolio (default)', tabTitle: 'MyInvestFolio', favicon: null /* keep bundled */ },
  { key: 'personal_records', label: 'Personal Records',        tabTitle: 'Personal Records', favicon: RECORDS },
  { key: 'personal_notes',   label: 'Personal Notes',          tabTitle: 'Personal Notes',   favicon: NOTEBOOK },
  { key: 'reading_list',     label: 'Reading List',            tabTitle: 'Reading List',     favicon: BOOK },
  { key: 'documents',        label: 'Documents',               tabTitle: 'Documents',        favicon: FOLDER },
  { key: 'custom',           label: 'Other — type your own',   tabTitle: null /* filled from prefs.browser_disguise_custom */, favicon: FOLDER },
];

export const FILENAME_PREFIXES = [
  { key: 'myinvestfolio',    label: 'MyInvestFolio (default)', slug: 'myinvestfolio' },
  { key: 'personal_notes',   label: 'Personal Notes',           slug: 'Personal-Notes' },
  { key: 'personal_records', label: 'Personal Records',        slug: 'Personal-Records' },
  { key: 'reading_list',     label: 'Reading List',             slug: 'Reading-List' },
  { key: 'meeting_notes',    label: 'Meeting Notes',            slug: 'Meeting-Notes' },
  { key: 'custom',           label: 'Other — type your own',    slug: null /* filled from prefs.filename_prefix_custom */ },
];

/**
 * Sanitise a custom filename prefix as the user types.
 * Rules mirror the backend regex: letters, digits, and hyphens only,
 * 3–20 chars, no leading/trailing hyphen. Spaces auto-convert to hyphens.
 */
export function sanitizeCustomPrefix(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9-]/g, '')
    .replace(/^-+/, '')
    .slice(0, 20);
}

export function isValidCustomPrefix(value) {
  return typeof value === 'string' && /^(?!-)[A-Za-z0-9-]{3,20}(?<!-)$/.test(value);
}

/**
 * Sanitise a custom browser-tab title as the user types.
 * Tab titles are display-only (they never end up in a URL or filename),
 * so we allow spaces, hyphens and apostrophes in addition to letters
 * and digits. 3–24 chars, no leading/trailing separator.
 */
export function sanitizeCustomTabTitle(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/[^A-Za-z0-9 '\-]/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[ '\-]+/, '')
    .slice(0, 24);
}

export function isValidCustomTabTitle(value) {
  return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9 '\-]{1,22}[A-Za-z0-9]$/.test(value);
}

export function filenamePrefixFor(key) {
  const match = FILENAME_PREFIXES.find((f) => f.key === key);
  return (match && match.slug) || 'myinvestfolio';
}

/**
 * Resolve the effective filename slug for the current preferences.
 * When the member has picked "Other", we use their custom value; if it's
 * empty or invalid we quietly fall back to the default so downloads keep
 * working.
 */
export function resolveFilenamePrefix(prefs) {
  if (!prefs) return 'myinvestfolio';
  if (prefs.filename_prefix === 'custom') {
    const cust = (prefs.filename_prefix_custom || '').trim();
    return isValidCustomPrefix(cust) ? cust : 'myinvestfolio';
  }
  return filenamePrefixFor(prefs.filename_prefix);
}

export function browserDisguiseFor(key) {
  return BROWSER_DISGUISES.find((d) => d.key === key) || BROWSER_DISGUISES[0];
}

/**
 * Resolve the effective browser-disguise preset. If the member picked
 * "Other" and typed a valid title, we return a synthetic preset with
 * their title. Otherwise we fall through to the standard preset lookup.
 */
export function resolveBrowserDisguise(prefs) {
  const key = prefs && prefs.browser_disguise;
  if (key === 'custom') {
    const cust = ((prefs && prefs.browser_disguise_custom) || '').trim();
    if (isValidCustomTabTitle(cust)) {
      const template = browserDisguiseFor('custom');
      return { ...template, tabTitle: cust };
    }
    // Invalid/empty custom title falls back to the default preset.
    return browserDisguiseFor('myinvestfolio');
  }
  return browserDisguiseFor(key);
}
